import { PropertyHelper } from '@/helpers/property.helper';
import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Document, DocumentCategory, RealProperty, VaultService } from '@eemo/api-client';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ModalBaseComponent } from '../base/modal-base/modal-base.component';

export class DocSelectDialogData {
  property: RealProperty;
  category?: DocumentCategory;
  multiple = false;
}

@Component({
  selector: 'app-doc-select-dialog',
  templateUrl: './doc-select-dialog.component.html',
  styleUrls: ['./doc-select-dialog.component.scss'],
  standalone: true,
  imports: [
    ModalBaseComponent,
    NgTemplateOutlet,
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    NgFor,
    NgIf,
    MatProgressBarModule,
    MatButtonModule,
    TranslateModule
  ]
})
export class DocSelectDialogComponent implements OnInit, OnDestroy {
  dialogTitle = 'docSelectDialog.title';
  isLoading = false;
  getDocumentsSub = new Subscription();

  documents: Document[] = [];
  docsDisabled: string[] = [];
  categoryName = '';

  get form_documents() {
    return this.form.get('documents') as FormControl<Document[]>;
  }

  form = this.fb.group({
    documents: [[], Validators.required]
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DocSelectDialogData,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<DocSelectDialogComponent>,
    private vaultService: VaultService,
    private translate: TranslateService,
    private propHelper: PropertyHelper
  ) {}

  ngOnInit(): void {
    this.getRealPropertyDocuments();
    if (this.data.category) {
      this.categoryName = this.propHelper.getDocumentCategoryName(this.data.category);
    }
  }

  ngOnDestroy(): void {
    this.getDocumentsSub.unsubscribe();
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.dialogRef.close(this.form_documents.value);
    }
  }

  afterGetDocs(docs: Document[]) {
    this.documents = docs;
    this.docsDisabled = docs.filter((d) => d.linkObjectId !== null).map((d) => d.uid);
  }

  onClickDocument(event: MatCheckboxChange, item: Document) {
    const checked = event.checked;
    if (!this.data.multiple) {
      this.removeAllDocument();
    }
    if (checked) {
      this.addDocument(item);
    } else {
      this.removeDocument(item);
    }
  }

  addDocument(doc: Document) {
    const docs = [...this.form_documents.value];
    docs.push(doc);
    this.form_documents.setValue(docs);
  }

  removeDocument(doc: Document) {
    const i = this.form_documents.value.findIndex((d: Document) => d.uid === doc.uid);
    if (i >= 0) {
      const docs = [...this.form_documents.value];
      docs.splice(i, 1);
      this.form_documents.setValue(docs);
    }
  }

  removeAllDocument() {
    this.form_documents.setValue([]);
  }

  // ---------------------------------------- //
  //                   APIS                   //
  // ---------------------------------------- //
  getRealPropertyDocuments() {
    this.isLoading = true;
    this.getDocumentsSub = this.vaultService
      .getRealPropertyDocuments(this.data.property.uid, this.data.category)
      .subscribe({
        next: (docs: Document[]) => {
          this.isLoading = false;
          this.afterGetDocs(docs);
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }
}
